import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import enTranslation from '../locales/en.json';
import deTranslation from '../locales/de.json';

/**
 * Single i18next instance for flameback-ui.
 *
 * English (`en`) and German (`de`) catalogs are statically imported and bundled,
 * so switching language is synchronous — no async load, Suspense stays disabled
 * (`react.useSuspense: false`), which also avoids a Suspense-boundary requirement
 * under React 19. Each locale catalog is a composed artifact (`src/locales/<lng>.json`)
 * built from `src/locales/<lng>/*.json` fragments by `npm run i18n:compose`. Once a
 * 3rd+ locale ships, switch to lazy per-language loading — see src/locales/README.md.
 */
export const resources = {
  en: { translation: enTranslation },
  de: { translation: deTranslation },
} as const;

/** Registered locales. English is the fallback; German is bundled alongside it. */
export const SUPPORTED_LANGUAGES = ['en', 'de'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/**
 * German stays bundled but is not user-selectable until its translations are
 * reviewed. Opt in per environment with the runtime flag `ENABLE_GERMAN=true`
 * (window.__RUNTIME_CONFIG__, generated by docker-entrypoint.sh).
 */
export const isGermanEnabled = (): boolean =>
  typeof window !== 'undefined' &&
  String(window.__RUNTIME_CONFIG__?.ENABLE_GERMAN ?? '').toLowerCase() === 'true';

/** Locales the app may actually switch to — a subset of SUPPORTED_LANGUAGES. */
export const getEnabledLanguages = (): SupportedLanguage[] =>
  SUPPORTED_LANGUAGES.filter((lng) => lng !== 'de' || isGermanEnabled());

const isEnabled = (code: string): code is SupportedLanguage =>
  (getEnabledLanguages() as string[]).includes(code);

/**
 * Resolve the initial UI language, in priority order:
 *   1. a `?lng=<code>` query param (handy for QA without changing OS settings),
 *   2. the browser's preferred languages (`navigator.languages` / `navigator.language`).
 * Region subtags are ignored (`de-DE` → `de`); anything unknown or not enabled
 * (see `isGermanEnabled`) falls back to English.
 * The test runner (`NODE_ENV==='test'`) always resolves to English so the existing
 * English text assertions stay deterministic regardless of the jsdom navigator.
 */
export const detectInitialLanguage = (): SupportedLanguage => {
  if (process.env.NODE_ENV === 'test') return DEFAULT_LANGUAGE;
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  try {
    const candidates: string[] = [];
    const override = new URLSearchParams(window.location.search).get('lng');
    if (override) candidates.push(override);
    const navPrefs = window.navigator.languages?.length
      ? window.navigator.languages
      : [window.navigator.language];
    candidates.push(...navPrefs);
    for (const candidate of candidates) {
      const base = (candidate || '').toLowerCase().split('-')[0];
      if (isEnabled(base)) return base;
    }
  } catch {
    // navigator/URL access can throw in exotic environments — default to English.
  }
  return DEFAULT_LANGUAGE;
};

/**
 * `saveMissing` + `missingKeyHandler` surface untranslated keys during the
 * migration. Kept to development only so production and the test runner stay
 * silent (and tests do not depend on console output).
 */
const isDev = process.env.NODE_ENV === 'development';

i18n.use(initReactI18next).init({
  resources,
  lng: detectInitialLanguage(),
  fallbackLng: 'en',
  supportedLngs: getEnabledLanguages(),
  // React already escapes interpolated values, so i18next must not double-escape.
  interpolation: { escapeValue: false },
  // Return the key (never `null`) for a missing value so the UI degrades to a
  // visible key rather than an empty node.
  returnNull: false,
  react: { useSuspense: false },
  saveMissing: isDev,
  missingKeyHandler: isDev
    ? (_lngs, ns, key) => {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${ns}:${key}`);
      }
    : undefined,
});

export default i18n;
